'use client';

import { CSSProperties, Fragment, useEffect, useMemo, useRef, useState } from 'react';

import { css } from 'styled-system/css';

import { useIntersectionObserverHealth } from '@/hooks/use-intersection-observer-health';

const VIEWPORT_MARGIN = '0px 0px -10% 0px';

// Keyframes `sh-home-line-in` live in the X5 block of src/app/globals.css (rule 9).
const rootClass = css({
  display: 'inline',
  '& [data-lr-word]': {
    display: 'inline-block',
    overflow: 'hidden',
    verticalAlign: 'top',
    // room for descenders inside the clipping mask
    paddingBottom: '0.14em',
    marginBottom: '-0.14em',
  },
  '& [data-lr-inner]': { display: 'inline-block' },
  '&[data-lr-ready] [data-lr-inner]': { transform: 'translateY(112%)', opacity: 0 },
  '&[data-lr-animate] [data-lr-inner]': {
    animation:
      'sh-home-line-in 0.85s cubic-bezier(0.22, 0.61, 0.36, 1) calc(var(--lr-line, 0) * 0.12s + var(--lr-delay, 0s)) forwards',
  },
});

type Props = {
  text: string;
  /** Extra delay (s) before the first line starts — to sequence a heading before its paragraph. */
  delay?: number;
};

/**
 * Panda port of wrappers/LineReveal: reveals copy line by line, each line rising out of a clipping
 * mask. Words are grouped into visual lines by their offsetTop inside a ResizeObserver callback.
 * SSR HTML is plain, fully visible text (the words are only hidden once measured on the client).
 */
export const LineReveal = ({ text, delay = 0 }: Props) => {
  const rootRef = useRef<HTMLSpanElement | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isStatic, setIsStatic] = useState(false);
  const ioHealth = useIntersectionObserverHealth();

  const words = useMemo(() => text.split(/\s+/).filter(Boolean), [text]);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setIsStatic(true);
      return undefined;
    }
    const root = rootRef.current;
    if (!root) return undefined;

    setIsReady(false);
    setIsVisible(false);

    const resizeObserver = new ResizeObserver(() => {
      const wordEls = Array.from(root.querySelectorAll<HTMLElement>('[data-lr-word]'));
      let line = -1;
      let lastTop: number | null = null;
      wordEls.forEach((el) => {
        const top = el.offsetTop;
        if (lastTop === null || Math.abs(top - lastTop) > 2) {
          line += 1;
          lastTop = top;
        }
        el.style.setProperty('--lr-line', String(line));
      });
      setIsReady(true);
    });
    resizeObserver.observe(root);

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setIsVisible(true);
          intersectionObserver.disconnect();
        }
      },
      { rootMargin: VIEWPORT_MARGIN, threshold: 0.1 },
    );
    intersectionObserver.observe(root);

    return () => {
      resizeObserver.disconnect();
      intersectionObserver.disconnect();
    };
  }, [text]);

  if (isStatic || ioHealth === false) return <>{text}</>;

  return (
    <span
      ref={rootRef}
      className={rootClass}
      data-lr-ready={isReady ? '' : undefined}
      data-lr-animate={isReady && isVisible ? '' : undefined}
      style={{ '--lr-delay': `${delay}s` } as CSSProperties}
    >
      {words.map((word, index) => (
        <Fragment key={`${word}-${index}`}>
          <span data-lr-word="">
            <span data-lr-inner="">{word}</span>
          </span>
          {index < words.length - 1 ? ' ' : null}
        </Fragment>
      ))}
    </span>
  );
};
